/**
 * Firebase Brands Service
 * Handles Firestore brand operations
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '@/config/firebase.config';
import { Brand } from '@/types/product.types';
import { brands as staticBrands, BrandData, BrandFeature } from '@/data/brands';

/**
 * Firebase brand document type (extends BrandData with Firebase-specific fields)
 */
export interface FirebaseBrandData extends Omit<BrandData, 'productCount' | 'createdAt' | 'updatedAt'> {
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Brand form data for create/update operations
 */
export interface BrandFormData {
  id: Brand;
  name: string;
  slug: string;
  description: string;
  fullDescription: string;
  logo: string;
  image: string;
  bannerImage: string;
  color: string;
  tagline: string;
  foundedYear: number;
  headquarters: string;
  country: string;
  specializations: string[];
  keyProducts: string[];
  certifications: string[];
  features: BrandFeature[];
  website?: string;
  isActive: boolean;
  isFeatured: boolean;
  order: number;
}

/**
 * Generate a URL-friendly slug from brand name
 */
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Get all brands from Firebase
 */
export const getAllBrands = async (): Promise<BrandData[]> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.BRANDS),
      orderBy('order', 'asc')
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Return static brands if none in Firebase yet
      return staticBrands;
    }

    return snapshot.docs.map(doc => ({
      ...doc.data(),
      docId: doc.id,
      productCount: 0,
    })) as (BrandData & { docId: string })[];
  } catch (error) {
    console.error('Error fetching brands:', error);
    return staticBrands;
  }
};

/**
 * Get active brands only (with products or featured)
 */
export const getActiveBrands = async (): Promise<BrandData[]> => {
  try {
    const allBrands = await getAllBrands();
    return allBrands.filter(b => b.isActive);
  } catch (error) {
    console.error('Error fetching active brands:', error);
    return staticBrands.filter(b => b.isActive);
  }
};

/**
 * Get featured brands only
 */
export const getFeaturedBrands = async (): Promise<BrandData[]> => {
  try {
    const allBrands = await getAllBrands();
    return allBrands.filter(b => b.isActive && b.isFeatured);
  } catch (error) {
    console.error('Error fetching featured brands:', error);
    return staticBrands.filter(b => b.isActive && b.isFeatured);
  }
};

/**
 * Get brand by document ID
 */
export const getBrandById = async (brandId: string): Promise<BrandData | null> => {
  try {
    const brandDoc = await getDoc(doc(db, COLLECTIONS.BRANDS, brandId));

    if (!brandDoc.exists()) {
      return null;
    }

    return {
      ...brandDoc.data(),
      docId: brandDoc.id,
      productCount: 0,
    } as BrandData & { docId: string };
  } catch (error) {
    console.error('Error fetching brand:', error);
    throw new Error('Failed to fetch brand');
  }
};

/**
 * Get brand by slug
 */
export const getBrandBySlug = async (slug: string): Promise<BrandData | null> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.BRANDS),
      where('slug', '==', slug),
      limit(1)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Try static brands as fallback
      const staticBrand = staticBrands.find(b => b.slug === slug);
      return staticBrand || null;
    }

    const docData = snapshot.docs[0];
    return {
      ...docData.data(),
      docId: docData.id,
      productCount: 0,
    } as BrandData & { docId: string };
  } catch (error) {
    console.error('Error fetching brand by slug:', error);
    throw new Error('Failed to fetch brand');
  }
};

/**
 * Get brand by Brand enum ID
 */
export const getBrandByEnumId = async (id: Brand): Promise<BrandData | null> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.BRANDS),
      where('id', '==', id),
      limit(1)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      // Try static brands as fallback
      const staticBrand = staticBrands.find(b => b.id === id);
      return staticBrand || null;
    }

    const docData = snapshot.docs[0];
    return {
      ...docData.data(),
      docId: docData.id,
      productCount: 0,
    } as BrandData & { docId: string };
  } catch (error) {
    console.error('Error fetching brand by enum ID:', error);
    throw new Error('Failed to fetch brand');
  }
};

/**
 * Create new brand
 */
export const createBrand = async (formData: BrandFormData): Promise<string> => {
  try {
    const now = Timestamp.now();
    const slug = formData.slug || generateSlug(formData.name);

    const brandData: FirebaseBrandData = {
      id: formData.id,
      name: formData.name,
      slug,
      description: formData.description,
      fullDescription: formData.fullDescription,
      logo: formData.logo,
      image: formData.image,
      bannerImage: formData.bannerImage,
      color: formData.color,
      tagline: formData.tagline,
      foundedYear: formData.foundedYear,
      headquarters: formData.headquarters,
      country: formData.country,
      specializations: formData.specializations,
      keyProducts: formData.keyProducts,
      certifications: formData.certifications,
      features: formData.features,
      website: formData.website || '',
      isActive: formData.isActive,
      isFeatured: formData.isFeatured,
      order: formData.order,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.BRANDS), brandData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating brand:', error);
    throw new Error('Failed to create brand');
  }
};

/**
 * Update existing brand
 */
export const updateBrand = async (
  docId: string,
  formData: Partial<BrandFormData>
): Promise<void> => {
  try {
    const updates: Partial<FirebaseBrandData> & { updatedAt: Timestamp } = {
      updatedAt: Timestamp.now(),
    };

    if (formData.name !== undefined) {
      updates.name = formData.name;
      updates.slug = generateSlug(formData.name);
    }
    if (formData.slug !== undefined) {
      updates.slug = formData.slug;
    }
    if (formData.description !== undefined) {
      updates.description = formData.description;
    }
    if (formData.fullDescription !== undefined) {
      updates.fullDescription = formData.fullDescription;
    }
    if (formData.logo !== undefined) {
      updates.logo = formData.logo;
    }
    if (formData.image !== undefined) {
      updates.image = formData.image;
    }
    if (formData.bannerImage !== undefined) {
      updates.bannerImage = formData.bannerImage;
    }
    if (formData.color !== undefined) {
      updates.color = formData.color;
    }
    if (formData.tagline !== undefined) {
      updates.tagline = formData.tagline;
    }
    if (formData.foundedYear !== undefined) {
      updates.foundedYear = formData.foundedYear;
    }
    if (formData.headquarters !== undefined) {
      updates.headquarters = formData.headquarters;
    }
    if (formData.country !== undefined) {
      updates.country = formData.country;
    }
    if (formData.specializations !== undefined) {
      updates.specializations = formData.specializations;
    }
    if (formData.keyProducts !== undefined) {
      updates.keyProducts = formData.keyProducts;
    }
    if (formData.certifications !== undefined) {
      updates.certifications = formData.certifications;
    }
    if (formData.features !== undefined) {
      updates.features = formData.features;
    }
    if (formData.website !== undefined) {
      updates.website = formData.website;
    }
    if (formData.isActive !== undefined) {
      updates.isActive = formData.isActive;
    }
    if (formData.isFeatured !== undefined) {
      updates.isFeatured = formData.isFeatured;
    }
    if (formData.order !== undefined) {
      updates.order = formData.order;
    }

    await updateDoc(doc(db, COLLECTIONS.BRANDS, docId), updates);
  } catch (error) {
    console.error('Error updating brand:', error);
    throw new Error('Failed to update brand');
  }
};

/**
 * Delete brand
 */
export const deleteBrand = async (docId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.BRANDS, docId));
  } catch (error) {
    console.error('Error deleting brand:', error);
    throw new Error('Failed to delete brand');
  }
};

/**
 * Get brands count
 */
export const getBrandsCount = async (): Promise<number> => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.BRANDS));
    return snapshot.size;
  } catch (error) {
    console.error('Error fetching brands count:', error);
    return 0;
  }
};

/**
 * Migrate all static brands to Firebase
 */
export const migrateBrands = async (): Promise<number> => {
  try {
    let migratedCount = 0;

    for (const brand of staticBrands) {
      const now = Timestamp.now();

      const brandData: FirebaseBrandData = {
        id: brand.id,
        name: brand.name,
        slug: brand.slug,
        description: brand.description,
        fullDescription: brand.fullDescription,
        logo: brand.logo,
        image: brand.image,
        bannerImage: brand.bannerImage,
        color: brand.color,
        tagline: brand.tagline,
        foundedYear: brand.foundedYear,
        headquarters: brand.headquarters,
        country: brand.country,
        specializations: brand.specializations,
        keyProducts: brand.keyProducts,
        certifications: brand.certifications,
        features: brand.features,
        website: brand.website || '',
        isActive: brand.isActive,
        isFeatured: brand.isFeatured,
        order: brand.order,
        createdAt: now,
        updatedAt: now,
      };

      await addDoc(collection(db, COLLECTIONS.BRANDS), brandData);
      migratedCount++;
    }

    return migratedCount;
  } catch (error) {
    console.error('Error migrating brands:', error);
    throw new Error('Failed to migrate brands');
  }
};

/**
 * Delete all brands from Firebase
 */
export const deleteAllBrands = async (): Promise<number> => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.BRANDS));
    
    const deletePromises = snapshot.docs.map(docSnapshot => 
      deleteDoc(docSnapshot.ref)
    );
    
    await Promise.all(deletePromises);
    return snapshot.size;
  } catch (error) {
    console.error('Error deleting all brands:', error);
    throw new Error('Failed to delete all brands');
  }
};
